<template>
  <el-dialog
    :title="$t('새 메뉴로 등록')"
    :visible="visible"
    width="640px"
    :close-on-click-modal="false"
    @close="close">
    <div v-if="link">
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item :label="$t('플랫폼')">{{ platformName(link.platform) }}</el-descriptions-item>
        <!-- `이름(번호)` 한 줄 — 조립 규칙은 utils/deliveryMenuSync 한 곳 (§16.179) -->
        <el-descriptions-item :label="$t('가게')">{{ shop(link) }}</el-descriptions-item>
        <el-descriptions-item :label="$t('배달앱 메뉴명')">
          <span v-if="link.observedName">{{ link.observedName }}</span>
          <span v-else class="dm-mat__weak">
            {{ link.menuNameKey }}
            <el-tag size="mini" type="info">{{ $t('정규화 키 (원문 아님)') }}</el-tag>
          </span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('배달앱 가격')">{{ priceText }}</el-descriptions-item>
        <el-descriptions-item :label="$t('배달앱 카테고리')">
          <span v-if="link.observedCategoryNm">{{ link.observedCategoryNm }}</span>
          <span v-else class="dm-mat__weak">{{ $t('읽지 못함') }}</span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('배달앱 옵션')">
          <span :class="{ 'dm-mat__weak': !hasOptions }">{{ optionText }}</span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('관측 시각')">{{ formatDate(link.observedAt) }}</el-descriptions-item>
      </el-descriptions>

      <!--
        **옵션은 관측한 만큼만 따라온다** (§16.195).

        배달앱 원본의 옵션그룹·옵션 항목·추가금액·최소최대 선택수는 `메뉴 가져오기` 가 읽어 둔 것을
        그대로 붙인다. 읽은 적이 없으면 옵션 없는 메뉴가 만들어지는데, 그 사실을 누른 **뒤에** 알게
        되는 것이 이 CS(2026-08-18)의 시작이었다. 그래서 누르기 전에 말하고, 할 수 있는 일도 함께 말한다.
      -->
      <el-alert
        v-if="!hasOptions"
        class="dm-mat__alert"
        type="warning"
        :closable="false"
        show-icon
        :title="
          $t(
            '이 메뉴의 배달앱 옵션을 아직 읽지 못해 옵션 없이 만들어집니다. POS 에서 이 가게의 메뉴 가져오기를 한 번 실행한 뒤 다시 시도하면 옵션도 함께 등록됩니다.'
          )
        "></el-alert>

      <!--
        **맞추기는 다음 회차에도 계속된다** (§16.197⑤).

        바로 위 안내가 말하는 「메뉴 가져오기」는 이번 한 번의 조작이 아니라 **이 메뉴의 옵션이
        앞으로도 배달앱을 따라간다**는 뜻이다(`MenuOptionLinkWriter.align` — 전부 지우고 관측 집합을
        다시 넣는다). 그래서 옵션을 오더엔에서 지워도 배달앱에 남아 있으면 다음 회차에 되돌아온다.

        **관측 유무와 무관하게 뜬다** — 지금 옵션을 읽지 못한 메뉴도 다음 가져오기부터 같은 규칙을
        받는다. 문구는 `utils/deliveryMenuNotes` 한 곳이 소유한다.
      -->
      <el-alert class="dm-mat__alert" type="info" :closable="false" show-icon :title="optionImportNote"></el-alert>

      <el-alert
        class="dm-mat__alert"
        type="warning"
        :closable="false"
        show-icon
        :title="
          $t(
            '배달앱에서 읽어 온 이름·가격으로 오더엔 메뉴를 새로 만들고 이 연결을 확정합니다. 만든 뒤 값을 고치는 곳은 메뉴 수정 화면입니다.'
          )
        "></el-alert>

      <!--
        **카테고리는 사장님이 답할 수 있어야 한다** (§16.178).

        링크는 플랫폼 카테고리의 id 만 갖고 이름을 갖지 않던 시절 이 버튼은 dev 실데이터에서
        ORPHAN 145건 중 143건이 "카테고리를 모른다" 400 이었다. 카테고리는 POS 진열·오더톡 노출이
        전부 걸리는 축이라 서버가 답할 수 없을 때 답할 자리가 없으면 그 행은 어느 버튼으로도
        해소되지 않는다.

        **고르기와 이름 짓기를 함께 쓸 수 있다** (§16.186) — 오더엔 메뉴는 여러 카테고리에 동시에
        속할 수 있으므로 "이 셋에 넣고, 없으면 하나 더 만들어 거기도 넣어라" 가 모순이 아니다.
        20차의 라디오(둘 중 하나)는 축이 단수였기에 필요했던 제약이다.
      -->
      <div class="dm-mat__section">
        <div class="dm-mat__label">{{ $t('메뉴를 넣을 카테고리') }}</div>

        <el-alert
          v-if="!link.observedCategoryNm"
          class="dm-mat__alert"
          type="info"
          :closable="false"
          show-icon
          :title="$t('배달앱 카테고리 이름을 아직 읽지 못했습니다. 이 메뉴를 넣을 카테고리를 정해 주세요.')"></el-alert>

        <template v-if="categoryPickable">
          <div class="dm-mat__sublabel">{{ $t('기존 카테고리에서 선택 (여러 개 가능)') }}</div>
          <el-checkbox-group v-model="categoryCds" class="dm-mat__checks">
            <el-checkbox
              v-for="category in categories"
              :key="category.categoryCd"
              :label="category.categoryCd"
              border
              size="small">
              {{ category.categoryNm }}
            </el-checkbox>
          </el-checkbox-group>
        </template>

        <div class="dm-mat__sublabel">{{ $t('새 카테고리 이름 (선택)') }}</div>
        <el-input
          v-model="categoryNm"
          size="small"
          clearable
          maxlength="50"
          show-word-limit
          :placeholder="$t('카테고리 이름 — 같은 이름이 있으면 그 카테고리를 씁니다')"></el-input>

        <div class="dm-mat__hint">
          {{
            categoryCds.length > 0
              ? $t('위에서 고른 카테고리에 넣습니다. 여기에 이름을 새로 적으면 그 카테고리에도 함께 넣습니다.')
              : $t('배달앱에서 읽어 온 이름이 기본값입니다. 바꾸면 그 이름의 카테고리가 만들어지거나 재사용됩니다.')
          }}
        </div>
        <div v-if="!categoryPickable" class="dm-mat__hint">
          {{ $t('형제 매장의 카테고리 목록은 이 화면에서 읽을 수 없어 이름으로만 정할 수 있습니다.') }}
        </div>
      </div>
    </div>

    <div slot="footer">
      <el-button size="small" @click="close">{{ $t('취소') }}</el-button>
      <el-button size="small" type="primary" :disabled="!canSubmit" :loading="saving" @click="materialize">
        {{ $t('새 메뉴로 등록') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import {
  canMaterializeCategory,
  DELIVERY_MENU_API,
  deliveryMenuPlatformName,
  deliveryMenuTimeText,
  hasObservedOptions,
  materializeCategoryBody,
  materializeFailure,
  observedOptionText,
  observedPriceText,
  shopText,
} from '@/utils/deliveryMenuSync'
import { OPTION_IMPORT_REALIGNS } from '@/utils/deliveryMenuNotes'
/**
 * ③ `새 메뉴로 등록` — `POST /links/{id}/materialize` (§16.172 · §16.178).
 *
 * **`bind` 와 뜻이 다르다.** `bind` 는 오더엔에 이미 있는 메뉴 행에 확정하고, 이쪽은 붙일 상대가
 * 없을 때 **관측으로 메뉴 행을 만들고 그 자리에서 확정**한다. 이 버튼이 없으면 요기요·쿠팡에만 있는
 * 메뉴는 어느 버튼으로도 해소되지 않는 행으로 남는다(§16.111).
 *
 * **본문에 실을 수 있는 것은 카테고리 하나뿐이고 그것도 선택이다.** 이름·가격을 실어 보내는 축을
 * 열면 "플랫폼에 있는 것을 들인다"가 아니라 새 메뉴 등록이 되고, 그것은 메뉴 CRUD 가 답하는
 * 질문이다. 카테고리만 예외인 이유는 그 축이 **오더엔 쪽 사실**(POS 진열·오더톡 노출)이라 플랫폼
 * 관측이 대신 답할 수 없는 경우가 있기 때문이다.
 *
 * 되돌리는 API 가 없으므로 이 대화상자 자체가 확인 절차다 — 만들어질 값과 카테고리를 함께 보여주고
 * 사장님이 그것을 보고 누른다.
 */
export default {
  name: 'DeliveryMenuMaterializeDialog',
  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
    /** `DeliveryMenuOrphanView` 응답 행 */
    link: { type: Object, default: null },
    /** 그 매장의 보이는 카테고리 `[{categoryCd, categoryNm}]` */
    categories: { type: Array, default: () => [] },
    /**
     * [categories] 가 **이 링크의 매장 것인가**.
     *
     * `GET /category/store` 는 세션 매장만 내리므로(경로에 매장 축이 없다) 형제 매장 링크에서는
     * 고를 목록이 없다. 남의 매장 카테고리를 보내면 서버가 400 이라, 그때는 이름 갈래만 연다 —
     * 이름은 서버가 **그 링크의 매장에서** 확보하므로 정확하다.
     */
    categoryPickable: { type: Boolean, default: false },
  },

  data() {
    return {
      categoryNm: '',
      categoryCds: [],
      saving: false,
    }
  },

  computed: {
    priceText() {
      // 관측 원문은 문자열이며 숫자가 아닐 수 있다 — 표기 규칙은 utils/deliveryMenuSync 가 소유한다
      return observedPriceText(this.link && this.link.observedPrice)
    },
    /** 옵션 문구·판정도 utils 한 곳이 소유한다 (§16.195) — 목록과 같은 말을 한다 */
    optionText() {
      return observedOptionText(this.link && this.link.observedOptionCount)
    },
    hasOptions() {
      return hasObservedOptions(this.link && this.link.observedOptionCount)
    },
    /** 되붙임 고지 — 문구의 정본은 `utils/deliveryMenuNotes` 다 (§16.197⑤) */
    optionImportNote() {
      return OPTION_IMPORT_REALIGNS
    },
    /**
     * **고른 것이 있으면 프리필된 이름은 쓰지 않는다** (§16.186).
     *
     * 관측 이름은 "아무것도 고르지 않았을 때의 기본값"이지 사장님이 입력한 값이 아니다. 두 축이
     * 합집합이 된 뒤로는 프리필을 그대로 실으면, `음료` 만 체크한 확정이 `음료` **와** 플랫폼
     * 어휘(`메인메뉴`)에 함께 들어가고 그 카테고리가 없으면 새로 만들어진다 — 오타 없이도 매
     * 확정마다 카테고리가 느는, §16.186 이 없애려던 바로 그 현상이다.
     *
     * 고친 뒤의 이름은 프리필이 아니라 사장님의 입력이므로 그대로 싣는다.
     */
    choice() {
      const usesPrefill = this.categoryNm === (this.link && this.link.observedCategoryNm)
      const categoryNm = this.categoryCds.length > 0 && usesPrefill ? '' : this.categoryNm
      return { categoryNm, categoryCds: this.categoryCds }
    },
    canSubmit() {
      if (!this.link) return false
      // 관측 이름조차 없는 링크는 **반드시** 고르게 한다 — 그대로 보내면 400 이다 (§16.178)
      return canMaterializeCategory(this.choice, this.link.observedCategoryNm)
    },
  },

  watch: {
    visible(open) {
      if (!open) return
      // **관측 이름이 기본값이다** — 있으면 채워 보여 주고(고칠 수 있게), 없으면 빈 칸으로 둔다
      this.categoryNm = (this.link && this.link.observedCategoryNm) || ''
      this.categoryCds = []
      this.saving = false
    },
  },

  methods: {
    platformName: deliveryMenuPlatformName,
    /** 샵 표기 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,

    formatDate(value) {
      return deliveryMenuTimeText(value, util.getDate)
    },

    async materialize() {
      if (!this.link || !this.canSubmit) return
      this.saving = true
      try {
        // 본문은 **고른 것 + 새 이름의 합집합이거나 없다** — 판정을 utils 한 곳이 소유한다 (§16.186).
        //
        // 오류 토스트는 옵트아웃한다: 409 는 이 요청에서 오류가 아니라 "그 사이 이미 연결됐다" 이고
        // 화면이 목록 재조회로 답한다. 인터셉터의 빨간 토스트가 함께 뜨면 화면이 두 말을 하므로,
        // 상태별 문구·톤을 `materializeFailure` 한 곳이 만든다 (§16.175).
        const res = await this.$axios.post(
          `${DELIVERY_MENU_API}/links/${this.link.linkId}/materialize`,
          materializeCategoryBody(this.choice),
          { showErrorMessage: false }
        )
        this.$emit('created', { link: this.link, created: res.data })
        this.close()
      } catch (e) {
        const failure = materializeFailure(e)
        this.$message({
          type: failure.tone,
          message: failure.hint ? `${failure.message} ${failure.hint}` : failure.message,
          duration: 8000,
        })
        // 목록이 낡았다는 뜻이면(409·404) 이 행은 이미 없다 — 대화상자를 닫고 목록을 다시 읽는다.
        // 400 은 사장님이 **이 자리에서** 카테고리를 고쳐 다시 누를 수 있으므로 열어 둔다.
        if (failure.refresh) {
          this.$emit('refresh')
          this.close()
        }
      } finally {
        this.saving = false
      }
    },

    close() {
      this.$emit('update:visible', false)
    },
  },
}
</script>

<style scoped>
.dm-mat__alert {
  margin-top: 12px;
}
.dm-mat__section {
  margin-top: 16px;
}
.dm-mat__label {
  font-weight: 600;
  margin-bottom: 8px;
}
.dm-mat__sublabel {
  margin: 10px 0 6px;
  font-size: 12px;
  color: #606266;
}
/* 카테고리가 많은 매장에서 대화상자가 끝없이 길어지지 않게 한다 — 목록은 자기 안에서 스크롤한다 */
.dm-mat__checks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 160px;
  overflow-y: auto;
}
.dm-mat__checks .el-checkbox {
  margin: 0;
}
.dm-mat__hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.dm-mat__weak {
  color: #909399;
}
</style>
